/*
  Wholesale café margin calculator — rendering + maths.

  Ported from _reference/wholesale-margin-calculator.html: the calculate()
  function below is the same maths as that file's calculate(), and compute()
  drives the same results panel in the same way. The only real differences
  are where the data comes from (merchant-entered "Matcha" and "Drink"
  section blocks, read from the <script type="application/json"> block in
  sections/wholesale-calculator.liquid, instead of the reference's hard-coded
  PRODUCTS/PRESETS arrays) and that every element is looked up scoped to this
  section's own root element (the same root-scoped-querySelector pattern
  assets/matcha-finder.js uses), so more than one instance of this section on
  a page can never cross-talk.

  No external libraries, no fetch calls, no localStorage. Respects
  prefers-reduced-motion (handled in assets/wholesale-calculator.css).
*/

(function () {
  'use strict';

  var X_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>';
  var PLUS_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true"><path d="M8 3v10M3 8h10"/></svg>';

  // Fixed "Other drink" quick-add — always available, not a merchant block.
  // The button reads "+ Other drink"; the row it creates defaults to the
  // name "New drink", so the two labels are kept separate here.
  var OTHER_DRINK_PRESET = { name: 'New drink', buttonLabel: 'Other drink', grams: 3, price: 7.0, perDay: 5, other: 1.0 };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function cssEscape(str) {
    return window.CSS && CSS.escape ? CSS.escape(str) : String(str).replace(/"/g, '\\"');
  }

  function initSection(root) {
    var instanceId = root.id.replace('wk-calc-', '');
    var dataEl = document.getElementById('wk-calc-data-' + instanceId);
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // malformed data — fail quietly rather than show a broken calculator
    }
    if (!data.matchas || !data.matchas.length) return; // nothing to calculate without at least one Matcha block

    var gstRate = typeof data.gstRate === 'number' && isFinite(data.gstRate) ? data.gstRate : 0.1;
    var bulkMinKg = data.bulkMinKg || 5;
    var weeksPerYear = data.weeksPerYear || 52;

    var currencyCode = (data.currency || 'AUD').toUpperCase();
    var moneyFmt2, moneyFmt0;
    try {
      moneyFmt2 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: currencyCode, minimumFractionDigits: 2, maximumFractionDigits: 2 });
      moneyFmt0 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: currencyCode, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } catch (e) {
      moneyFmt2 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
      moneyFmt0 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    function money(v) { return Math.abs(v) >= 1000 ? moneyFmt0.format(v) : moneyFmt2.format(v); }
    function moneyRound(v) { return moneyFmt0.format(v); }
    function intf(v) { return Math.round(v).toLocaleString('en-AU'); }
    function num(v) { var n = parseFloat(v); return isFinite(n) && n > 0 ? n : 0; }
    function kgLabel(g) { return g >= 1000 ? (g / 1000).toLocaleString('en-AU') + ' kg' : g + ' g'; }
    function fmtDays(v) { return v < 10 ? (Math.round(v * 10) / 10).toLocaleString('en-AU') : intf(v); }

    var drinkPresets = (data.drinkPresets || []).slice();
    drinkPresets.push(OTHER_DRINK_PRESET);

    function matchaById(id) {
      for (var i = 0; i < data.matchas.length; i++) if (data.matchas[i].id === id) return data.matchas[i];
      return data.matchas[0];
    }
    function defaultMatcha() {
      for (var i = 0; i < data.matchas.length; i++) if (data.matchas[i].selected) return data.matchas[i];
      return data.matchas[0];
    }
    function bulkAvailable(m) { return m.bulk != null && m.bulk < m.single; }

    var optionsEl = root.querySelector('.wk-calc__options');
    var orderEl = root.querySelector('.wk-calc__order');
    var orderBulkText = root.querySelector('.wk-calc__order-text-bulk');
    var orderNote = root.querySelector('.wk-calc__order-note');
    var orderInputs = Array.prototype.slice.call(root.querySelectorAll('.wk-calc__order-input'));
    var drinksEl = root.querySelector('.wk-calc__drinks');
    var addEl = root.querySelector('.wk-calc__add');
    var daysOut = root.querySelector('.wk-calc__days-out');
    var daysDec = root.querySelector('.wk-calc__days-dec');
    var daysInc = root.querySelector('.wk-calc__days-inc');
    var gstInput = root.querySelector('.wk-calc__gst-input');
    var resultsEl = root.querySelector('.wk-calc__results');
    var formEl = root.querySelector('.wk-calc__form');

    var uid = 0;
    function makeDrinkRow(preset) {
      uid += 1;
      return { id: instanceId + '-d' + uid, name: preset.name, grams: preset.grams, price: preset.price, perDay: preset.perDay, other: preset.other };
    }

    var state = {
      matchaId: defaultMatcha().id,
      bulk: true,
      drinks: [],
      days: data.daysOpenDefault || 6,
      gst: !!(gstInput && gstInput.checked)
    };
    (data.drinkPresets || []).forEach(function (p) {
      if (p.onMenu) state.drinks.push(makeDrinkRow(p));
    });
    if (!bulkAvailable(matchaById(state.matchaId))) state.bulk = false;

    /* ---------- Step 1: matcha options ---------- */
    function renderOptions() {
      optionsEl.innerHTML = data.matchas.map(function (m) {
        var price = state.bulk && bulkAvailable(m) ? m.bulk : m.single;
        var link = m.productUrl ? '<a class="wk-calc__option-link" href="' + esc(m.productUrl) + '">View product</a>' : '';
        return '<div class="wk-calc__option-wrap">' +
            '<label class="wk-calc__option">' +
              '<input type="radio" name="wk-calc-matcha-' + instanceId + '" value="' + esc(m.id) + '"' + (m.id === state.matchaId ? ' checked' : '') + '>' +
              '<span class="wk-calc__option-check" aria-hidden="true"></span>' +
              '<span class="wk-calc__option-name">' + esc(m.name) + '</span>' +
              (m.use ? '<span class="wk-calc__option-use">' + esc(m.use) + '</span>' : '') +
              '<span class="wk-calc__option-price">' + moneyRound(price) + ' <small>/kg</small></span>' +
              '<span class="wk-calc__option-bag">' + kgLabel(m.bagG) + ' bags</span>' +
            '</label>' + link +
          '</div>';
      }).join('');

      var m = matchaById(state.matchaId);
      var hasBulk = bulkAvailable(m);
      orderEl.hidden = !hasBulk;
      if (hasBulk) {
        var save = m.single - m.bulk;
        orderBulkText.textContent = 'Bulk · ' + bulkMinKg + ' kg+';
        orderNote.textContent = save > 0 ? 'Bulk saves ' + moneyRound(save) + ' per kg' : '';
      }
    }
    function syncOrderInputs() {
      orderInputs.forEach(function (r) { r.checked = (r.value === 'bulk') === state.bulk; });
    }
    optionsEl.addEventListener('change', function (e) {
      if (e.target.type !== 'radio') return;
      state.matchaId = e.target.value;
      if (!bulkAvailable(matchaById(state.matchaId))) {
        state.bulk = false;
      } else {
        var checkedOrder = orderInputs.filter(function (r) { return r.checked; })[0];
        state.bulk = checkedOrder ? checkedOrder.value === 'bulk' : true;
      }
      renderOptions();
      syncOrderInputs();
      var picked = optionsEl.querySelector('input[value="' + cssEscape(state.matchaId) + '"]');
      if (picked) picked.focus();
      compute();
    });
    orderInputs.forEach(function (r) {
      r.addEventListener('change', function () { state.bulk = r.value === 'bulk'; compute(); });
    });

    /* ---------- Step 2: drinks ---------- */
    function field(d, key, label, pre, suf, step) {
      var id = 'wk-calc-' + key + '-' + d.id;
      return '<label class="wk-calc__field" for="' + id + '"><span class="wk-calc__field-label">' + label + '</span>' +
        '<span class="wk-calc__field-box">' + (pre ? '<span class="wk-calc__field-affix">' + pre + '</span>' : '') +
        '<input type="number" inputmode="decimal" min="0" step="' + step + '" id="' + id + '" data-f="' + key + '" value="' + d[key] + '">' +
        (suf ? '<span class="wk-calc__field-affix">' + suf + '</span>' : '') + '</span></label>';
    }
    function renderDrinks() {
      if (!state.drinks.length) {
        drinksEl.innerHTML = '<p class="wk-calc__drinks-empty">No drinks yet. Add one below to start.</p>';
        return;
      }
      drinksEl.innerHTML = state.drinks.map(function (d) {
        var nameId = 'wk-calc-name-' + d.id;
        return '<div class="wk-calc__drink" data-id="' + d.id + '">' +
            '<div class="wk-calc__drink-top">' +
              '<label class="wk-calc__sr-only" for="' + nameId + '">Drink name</label>' +
              '<input class="wk-calc__drink-name" id="' + nameId + '" data-f="name" value="' + esc(d.name) + '" autocomplete="off">' +
              '<span class="wk-calc__chip" data-chip></span>' +
              '<button type="button" class="wk-calc__icon-btn" data-remove aria-label="Remove ' + esc(d.name || 'drink') + '">' + X_ICON + '</button>' +
            '</div>' +
            '<div class="wk-calc__drink-fields">' +
              field(d, 'grams', 'Matcha per drink', '', 'g', '0.5') +
              field(d, 'price', 'Menu price', '$', '', '0.10') +
              field(d, 'perDay', 'Sold per day', '', '', '1') +
              field(d, 'other', 'Milk, cup &amp; extras', '$', '', '0.05') +
            '</div>' +
            '<p class="wk-calc__drink-line" data-line></p>' +
          '</div>';
      }).join('');
    }
    drinksEl.addEventListener('input', function (e) {
      var row = e.target.closest('.wk-calc__drink'); if (!row) return;
      var d = state.drinks.filter(function (x) { return x.id === row.dataset.id; })[0]; if (!d) return;
      var f = e.target.dataset.f;
      if (f === 'name') {
        d.name = e.target.value;
        row.querySelector('[data-remove]').setAttribute('aria-label', 'Remove ' + (d.name || 'drink'));
      } else {
        d[f] = e.target.value;
      }
      compute();
    });
    drinksEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove]'); if (!btn) return;
      var id = btn.closest('.wk-calc__drink').dataset.id;
      state.drinks = state.drinks.filter(function (x) { return x.id !== id; });
      renderDrinks();
      compute();
      var first = addEl.querySelector('.wk-calc__pill-btn');
      if (first) first.focus();
    });

    function renderPresetButtons() {
      drinkPresets.forEach(function (p) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wk-calc__pill-btn';
        btn.innerHTML = PLUS_ICON + esc(p.buttonLabel || p.name);
        btn.addEventListener('click', function () {
          var d = makeDrinkRow(p);
          state.drinks.push(d);
          renderDrinks();
          compute();
          var inp = document.getElementById('wk-calc-name-' + d.id);
          if (inp) { inp.focus(); inp.select(); }
        });
        addEl.appendChild(btn);
      });
    }

    /* ---------- Step 3: trading ---------- */
    function renderDays() {
      daysOut.textContent = state.days;
      daysDec.disabled = state.days <= 1;
      daysInc.disabled = state.days >= 7;
    }
    daysDec.addEventListener('click', function () { state.days = Math.max(1, state.days - 1); renderDays(); compute(); });
    daysInc.addEventListener('click', function () { state.days = Math.min(7, state.days + 1); renderDays(); compute(); });
    if (gstInput) gstInput.addEventListener('change', function (e) { state.gst = e.target.checked; compute(); });

    /* ---------- Maths ----------
       Mirrors _reference/wholesale-margin-calculator.html's calculate(). */
    function calculate(s) {
      var m = matchaById(s.matchaId);
      var useBulk = s.bulk && bulkAvailable(m);
      var perKg = useBulk ? m.bulk : m.single;
      var perG = perKg / 1000;
      var t = { serves: 0, revenue: 0, matcha: 0, extras: 0, grams: 0 };
      var rows = s.drinks.map(function (d) {
        var g = num(d.grams), price = num(d.price), n = num(d.perDay), other = num(d.other);
        var net = s.gst ? price / (1 + gstRate) : price;
        var mc = g * perG;
        var cost = mc + other;
        var profit = net - cost;
        t.serves += n; t.revenue += net * n; t.matcha += mc * n; t.extras += other * n; t.grams += g * n;
        return { id: d.id, net: net, cost: cost, profit: profit, perDay: n, margin: net > 0 ? profit / net : null };
      });
      t.profit = t.revenue - t.matcha - t.extras;
      t.margin = t.revenue > 0 ? t.profit / t.revenue : null;
      var weekly = t.profit * s.days;
      var annual = weekly * weeksPerYear;
      var gramsYear = t.grams * s.days * weeksPerYear;
      return {
        matcha: m, useBulk: useBulk, perKg: perKg, perG: perG, rows: rows, t: t,
        daily: t.profit, weekly: weekly, annual: annual, monthly: annual / 12,
        perDrink: t.serves ? t.profit / t.serves : 0,
        matchaPerDrink: t.serves ? t.matcha / t.serves : 0,
        allInPerDrink: t.serves ? (t.matcha + t.extras) / t.serves : 0,
        gramsYear: gramsYear,
        bulkSaving: bulkAvailable(m) ? (gramsYear / 1000) * (m.single - m.bulk) : 0,
        bag: t.grams > 0 ? (function () {
          var f = m.bagG / t.grams; // trading days one bag covers
          return {
            days: f, weeks: f / s.days,
            drinks: m.bagG / (t.grams / t.serves),
            sales: t.revenue * f, cost: m.bagG * perG, extras: t.extras * f, profit: t.profit * f
          };
        })() : null,
        bulkWeeks: t.grams > 0 ? (bulkMinKg * 1000) / (t.grams * s.days) : null
      };
    }
    window.__wakuraWholesaleCalc = calculate; // exposed for checking, same as the reference's window.__wakuraCalc

    function q(sel) { return root.querySelector(sel); }
    function setMoney(sel, v) { var el = q(sel); if (!el) return; el.textContent = money(v); el.classList.toggle('is-loss', v < 0); }

    function compute() {
      var r = calculate(state);
      var t = r.t;

      r.rows.forEach(function (row) {
        var el = drinksEl.querySelector('.wk-calc__drink[data-id="' + cssEscape(row.id) + '"]'); if (!el) return;
        var chip = el.querySelector('[data-chip]'), line = el.querySelector('[data-line]');
        chip.classList.remove('is-low', 'is-empty');
        if (row.margin === null) { chip.textContent = 'Add a price'; chip.classList.add('is-empty'); line.textContent = ''; return; }
        chip.textContent = row.margin < 0 ? 'Loss on each' : Math.round(row.margin * 100) + '% margin';
        if (row.margin < 0.6) chip.classList.add('is-low');
        line.innerHTML = 'Costs <b>' + money(row.cost) + '</b> to make · <b>' + money(row.profit) + '</b> gross profit each' +
          (row.perDay ? ' · <b>' + money(row.profit * row.perDay) + '</b> a day' : '');
      });

      var empty = !(t.serves > 0 && t.revenue > 0);
      resultsEl.dataset.empty = empty ? 'true' : 'false';
      q('.wk-calc__r-empty').hidden = !empty;

      var hasBulk = bulkAvailable(r.matcha);
      q('.wk-calc__bulk').hidden = !hasBulk;

      if (empty) {
        ['.wk-calc__r-margin', '.wk-calc__lg-matcha', '.wk-calc__lg-extras', '.wk-calc__lg-profit', '.wk-calc__r-year', '.wk-calc__r-per',
         '.wk-calc__r-day', '.wk-calc__r-week', '.wk-calc__r-month', '.wk-calc__r-serves', '.wk-calc__r-matcha-cost', '.wk-calc__r-all-cost',
         '.wk-calc__bag-sales', '.wk-calc__bag-cost', '.wk-calc__bag-extras', '.wk-calc__bag-profit'].forEach(function (sel) {
          var el = q(sel); if (el) { el.textContent = '—'; el.classList.remove('is-loss'); }
        });
        ['.wk-calc__seg-matcha', '.wk-calc__seg-extras', '.wk-calc__seg-profit'].forEach(function (sel) {
          var el = q(sel); if (el) el.style.width = '0%';
        });
        q('.wk-calc__bag-title').textContent = 'One ' + kgLabel(r.matcha.bagG) + ' bag of ' + r.matcha.name;
        q('.wk-calc__bag-lead').textContent = '—';
        if (hasBulk) {
          q('.wk-calc__bulk-head').textContent = 'Bulk ordering';
          q('.wk-calc__bulk-body').textContent = '';
        }
        return;
      }

      q('.wk-calc__r-margin').textContent = Math.round(t.margin * 100);
      q('.wk-calc__r-margin').classList.toggle('is-loss', t.margin < 0);
      q('.wk-calc__r-margin-sub').textContent = 'After matcha, milk, cups' + (state.gst ? ' and GST' : '');
      q('.wk-calc__split-cap').textContent = 'Out of every $10 your matcha drinks bring in' + (state.gst ? ' (ex GST)' : '');

      var sm = t.matcha / t.revenue, se = t.extras / t.revenue, sp = t.profit / t.revenue;
      var widthSum = sm + se + Math.max(0, sp);
      q('.wk-calc__seg-matcha').style.width = (sm / widthSum * 100) + '%';
      q('.wk-calc__seg-extras').style.width = (se / widthSum * 100) + '%';
      q('.wk-calc__seg-profit').style.width = (Math.max(0, sp) / widthSum * 100) + '%';
      setMoney('.wk-calc__lg-matcha', sm * 10);
      setMoney('.wk-calc__lg-extras', se * 10);
      setMoney('.wk-calc__lg-profit', sp * 10);

      setMoney('.wk-calc__r-year', r.annual);
      setMoney('.wk-calc__r-per', r.perDrink);
      setMoney('.wk-calc__r-day', r.daily);
      setMoney('.wk-calc__r-week', r.weekly);
      setMoney('.wk-calc__r-month', r.monthly);
      q('.wk-calc__r-serves').textContent = intf(t.serves);
      q('.wk-calc__r-matcha-cost').textContent = money(r.matchaPerDrink);
      q('.wk-calc__r-all-cost').textContent = money(r.allInPerDrink);

      q('.wk-calc__bag-title').textContent = 'One ' + kgLabel(r.matcha.bagG) + ' bag of ' + r.matcha.name;
      if (r.bag) {
        q('.wk-calc__bag-lead').innerHTML = 'Lasts about <b>' + fmtDays(r.bag.days) + ' trading days</b> and makes about <b>' + intf(r.bag.drinks) + ' drinks</b>.';
        setMoney('.wk-calc__bag-sales', r.bag.sales);
        q('.wk-calc__bag-cost').textContent = '−' + money(r.bag.cost);
        q('.wk-calc__bag-extras').textContent = '−' + money(r.bag.extras);
        setMoney('.wk-calc__bag-profit', r.bag.profit);
      } else {
        q('.wk-calc__bag-lead').textContent = 'None of your drinks use matcha yet. Add grams per drink to see this.';
        ['.wk-calc__bag-sales', '.wk-calc__bag-cost', '.wk-calc__bag-extras', '.wk-calc__bag-profit'].forEach(function (sel) {
          var el = q(sel); if (el) el.textContent = '—';
        });
      }

      if (hasBulk) {
        var save = r.matcha.single - r.matcha.bulk;
        if (save > 0 && r.gramsYear > 0) {
          q('.wk-calc__bulk-head').textContent = state.bulk
            ? 'Bulk ordering saves you ' + moneyRound(r.bulkSaving) + ' a year'
            : 'Ordering ' + bulkMinKg + ' kg at a time would save you ' + moneyRound(r.bulkSaving) + ' a year';
          var w = r.bulkWeeks;
          var gw = t.grams * state.days;
          var usage = gw >= 1000 ? (Math.round(gw / 100) / 10).toLocaleString('en-AU') + ' kg' : Math.round(gw) + ' g';
          q('.wk-calc__bulk-body').textContent = 'You go through about ' + usage + ' a week, so ' + bulkMinKg + ' kg lasts roughly ' +
            (w < 1.5 ? 'a week' : fmtDays(w) + ' weeks') + '.';
        } else {
          q('.wk-calc__bulk-head').textContent = 'Bulk ordering';
          q('.wk-calc__bulk-body').textContent = 'Add drinks with matcha to see what bulk orders save you.';
        }
      }
    }

    /* ---------- Quote form ----------
       Mirrors the reference's summary(): matcha, order type, price per kg,
       days open, GST, each drink's numbers, margin, drinks a day, grams of
       matcha used a week, and yearly gross profit. */
    function summary() {
      var r = calculate(state), m = r.matcha;
      var lines = [
        'Matcha: ' + m.name + ' (' + (r.useBulk ? 'bulk, ' + bulkMinKg + ' kg+' : 'single bags') + ', ' + moneyRound(r.perKg) + '/kg)',
        'Trading: ' + state.days + ' days a week' + (state.gst ? ', menu prices include GST' : ''),
        '',
        'Menu:'
      ];
      state.drinks.forEach(function (d) {
        lines.push('- ' + (d.name || 'Drink') + ': ' + num(d.grams) + ' g, ' + moneyFmt2.format(num(d.price)) + ', ' + num(d.perDay) + ' a day, extras ' + moneyFmt2.format(num(d.other)));
      });
      if (r.t.revenue > 0) {
        lines.push('', 'Gross margin: ' + Math.round(r.t.margin * 100) + '%',
          'Matcha drinks a day: ' + intf(r.t.serves),
          'Matcha used a week: ' + Math.round(r.t.grams * state.days) + ' g',
          'Gross profit a year: ' + moneyRound(r.annual));
      }
      return lines.join('\n');
    }
    if (formEl) {
      formEl.addEventListener('submit', function () {
        var bodyField = formEl.querySelector('.wk-calc__form-body');
        if (bodyField) bodyField.value = summary();
      });
    }

    // Results panel can be taller than the window; let it scroll with the
    // page and stick once its bottom edge is in view, so nothing in it is
    // ever cut off (same approach as the reference's fitSticky()).
    function fitSticky() {
      var top = Math.min(20, window.innerHeight - resultsEl.offsetHeight - 20);
      resultsEl.style.setProperty('--wk-stick-top', top + 'px');
    }
    window.addEventListener('resize', fitSticky);
    if (window.ResizeObserver) new ResizeObserver(fitSticky).observe(resultsEl);

    renderOptions();
    renderDrinks();
    renderPresetButtons();
    renderDays();
    fitSticky();
    compute();
  }

  function init() {
    document.querySelectorAll('.wk-calc').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: Shopify swaps in fresh section HTML every time a setting or
  // block is changed, without re-running this file. Set the new copy up again
  // so the calculator keeps working while you edit prices in Customize.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('.wk-calc') : null;
    if (root) initSection(root);
  });
})();
